import { createApp } from 'vue';
import App from './App.vue';
import { SentryService } from './Services/Sentry';
import { ClientLogger } from './Services/Logger/ClientLogger';
import { ILogger } from '@/Contracts';
import {
    CoppiLocalisationPlugin,
    CoppiErrorReportingPlugin,
} from '@bookingkit/coppi';
import {
    bootstrapI18n,
    resolveLocale,
    updateDocumentLanguage,
} from './Services/TranslationService';
import '@bookingkit/coppi/dist/style.css';
import '@/css/default-override.css';
import BookingkitApiService from '@/bootstrap/Factories/BookingkitApiFactory';
import { SelfService } from '@/Services/BKAPI/SelfService';
import { API_V4, LOGGER } from '@/constants';

// Composition root: pick the logger implementation for this environment.
// Production reports to Sentry (no debug); everywhere else logs to the console.
const logger: ILogger = import.meta.env.PROD ? SentryService : ClientLogger;

const initApplication = async () => {
    const BKSelfServiceApplication = createApp(App);
    BKSelfServiceApplication.use(CoppiLocalisationPlugin, {});
    BKSelfServiceApplication.config.errorHandler = err => {
        if (err instanceof Error) {
            logger.logError(err);
        } else {
            logger.logError(new Error(String(err)));
        }
    };
    const { default: router } = await import('@/router');

    const params = new URLSearchParams(window.location.search);
    const queryLocale = params.get('locale') ?? undefined;
    const { locale: initialLocale } = resolveLocale({ queryLocale });
    const i18n = bootstrapI18n({ locale: initialLocale });
    updateDocumentLanguage(initialLocale);

    const authToken = params.get('token') || undefined;
    if (params.has('token')) {
        params.delete('token');
        const cleanUrl = `${window.location.pathname}${params.toString() ? `?${params.toString()}` : ''}${window.location.hash}`;
        history.replaceState({}, '', cleanUrl);
    }

    const bookingkitApiService = BookingkitApiService({
        logger,
        authToken,
    });
    BKSelfServiceApplication.provide<SelfService>(
        API_V4,
        bookingkitApiService.selfService
    );
    BKSelfServiceApplication.provide<ILogger>(LOGGER, logger);

    BKSelfServiceApplication.use(router);
    BKSelfServiceApplication.use(i18n);
    BKSelfServiceApplication.use(CoppiErrorReportingPlugin, {
        logException: logger.logError,
    });

    BKSelfServiceApplication.mount('#app');
};

initApplication().catch(error => {
    logger.logError(error instanceof Error ? error : new Error(String(error)));
});
